import {getParameterDefaultValuesNew} from '@/modules/patients/functions'
import {ParameterSchemaNew} from '@/modules/patients/schemas'
import {useGetInactivePrescribedNames} from '@/modules/products/trade-names/api'
import {isPrescribedTradeNameLocked} from '@/modules/products/trade-names/functions'
import {
  PrescribedTradeNameCombobox,
  useAutoSelectSingleInStockPrescribedName,
  usePrescribedNameSelectItems,
} from '@/modules/products/trade-names/prescribed-name-select'
import {PatientTherapySequenceMedicine} from '@cypro/engine/schema'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

export const PrescribedNameSelect = withForm({
  defaultValues: getParameterDefaultValuesNew(false, undefined, false) as ParameterSchemaNew,
  props: {} as {
    hasMultiple?: boolean
    index: number
    pharmacistValidation?: boolean
    savedMedicine?: PatientTherapySequenceMedicine | null
    disabled?: boolean
    /** The form's current No Aut Idem value; when on, the doctor fixed the product. */
    autIdem?: boolean
  },
  render: function Render({form, hasMultiple, index, pharmacistValidation, savedMedicine, disabled, autIdem}) {
    const {t} = useTranslate('common')

    // pharmacist validation: only the prescribed substance's products (with stock counts) are offered,
    // otherwise all active prescribed names
    const {items, inStockItems, noItemsInStock} = usePrescribedNameSelectItems(
      pharmacistValidation ? savedMedicine?.activeSubstanceId : undefined
    )
    // a prescription marked not-aut-idem has to be dispensed as prescribed; the lock follows the
    // switch's live value so turning No Aut Idem on locks the product immediately
    const lockedMedicine =
      savedMedicine && autIdem !== undefined ? {...savedMedicine, autIdem} : savedMedicine
    const isLocked = Boolean(pharmacistValidation && isPrescribedTradeNameLocked(lockedMedicine))

    useAutoSelectSingleInStockPrescribedName(
      form,
      index,
      inStockItems ?? [],
      Boolean(pharmacistValidation && !isLocked)
    )

    // a product saved before its trade names were made inactive is no longer offered; say why it must change
    const prescribedNameId = useStore(form.store, state => state.values.medicine?.[index]?.prescribedNameId)
    const [inactiveName] = useGetInactivePrescribedNames([prescribedNameId])

    return (
      <form.AppField name={`medicine[${index}].prescribedNameId`} mode="value">
        {() => (
          <PrescribedTradeNameCombobox
            label={`${t('drug')} ${hasMultiple ? index + 1 : ''}`}
            items={items}
            placeholder={noItemsInStock ? t('no_products_in_stock') : t('type_to_search')}
            disabled={disabled || isLocked || Boolean(pharmacistValidation && noItemsInStock)}
            helpText={isLocked ? t('alert.aut_idem_restriction.message') : undefined}
            errorMessage={
              inactiveName
                ? t('trade_name_inactive.message', '{name} is inactive in the catalogue. Choose an active trade name.', {
                    name: inactiveName,
                  })
                : undefined
            }
            required={pharmacistValidation}
          />
        )}
      </form.AppField>
    )
  },
})
